<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const identifyFields = [
  { field: 'Nama', type: 'Text', wajib: '✅ Ya (Tamu)', desc: 'Nama pemesan, ditampilkan di layar dapur & struk', example: '"Andi Wijaya"' },
  { field: 'No. HP', type: 'Text', wajib: '✅ Ya', desc: 'Untuk keperluan notifikasi & data pelanggan', example: '"081234567890"' },
  { field: 'Password', type: 'Text (tersembunyi)', wajib: '✅ Ya (khusus Member)', desc: 'Hanya diisi kalau pelanggan pilih login sebagai Member', example: '"••••••••"' },
]

const faq = [
  {
    q: 'Pesanan pelanggan sudah masuk, tapi kok belum muncul di layar dapur (KDS)?',
    a: 'Tiket dapur baru muncul di layar KDS setelah pesanan resmi terkonfirmasi di sistem. Untuk pembayaran "Bayar di Kasir", buka Penjualan → Daftar Order, cari order berstatus "Pending" tersebut, lalu konfirmasi lewat kartu "Terima Pembayaran" di halaman detailnya (lihat bagian "Cara Kasir Konfirmasi Bayar di Kasir" di atas). Untuk QRIS, tiket muncul otomatis setelah pembayaran berhasil.',
  },
  {
    q: 'Kenapa saya tidak bisa konfirmasi pembayaran self-order dari akun kasir saya?',
    a: 'Kartu "Terima Pembayaran" hanya muncul untuk pengguna yang punya izin membuat transaksi penjualan (permission Sales.create) — biasanya role Cashier, Manager, dan Owner sudah otomatis punya izin ini. Kalau tetap tidak muncul, cek pengaturan role akun tersebut di menu User & Role.',
  },
  {
    q: 'Pelanggan sudah scan QR tapi tidak bisa apa-apa / muncul "Gagal membuka sesi"?',
    a: 'Biasanya karena: (1) QR sudah pernah di-"Regenerate" sehingga kode lama tidak berlaku lagi — cetak ulang QR yang baru, (2) sesi sebelumnya sudah kedaluwarsa (otomatis berakhir setelah beberapa jam tidak aktif) — minta pelanggan scan ulang, atau (3) koneksi internet toko sedang bermasalah.',
  },
  {
    q: 'Kalau pelanggan me-refresh HP-nya di tengah pilih menu, apakah keranjangnya hilang?',
    a: 'Ya. Keranjang belanja pelanggan hanya tersimpan sementara di HP mereka dan belum tercatat di sistem sampai mereka menekan tombol checkout. Kalau HP di-refresh sebelum checkout, mereka harus pilih menu dari awal lagi. Ini untuk mencegah pesanan "sampah" yang menumpuk di sistem kalau pelanggan batal pesan.',
  },
  {
    q: 'Apakah pelanggan wajib jadi Member untuk bisa pesan?',
    a: 'Tidak. Pelanggan bisa pesan sebagai "Tamu" hanya dengan mengisi nama dan nomor HP, tanpa perlu daftar akun. Opsi "Member" disediakan untuk pelanggan yang sudah punya akun (biasanya untuk dapat poin/promo, tergantung apakah fitur membership diaktifkan di toko Anda).',
  },
  {
    q: 'Bisakah pelanggan bayar QRIS langsung dari HP mereka?',
    a: 'Fitur ini butuh Payment Gateway (mis. Midtrans) yang sudah disetel di pengaturan toko. Kalau belum disetel, sarankan pelanggan pilih opsi "Bayar di Kasir" saat checkout — mereka tetap bisa pesan lewat HP, hanya pembayarannya tetap manual ke kasir.',
  },
  {
    q: 'Satu meja bisa dipakai pesan berkali-kali dalam satu kunjungan?',
    a: 'Bisa. Selama sesi meja masih aktif (belum kedaluwarsa), pelanggan bisa scan QR yang sama lagi untuk menambah pesanan — misalnya mau nambah minuman setelah makanan pertama habis.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/f-b-modern" class="text-decoration-none text-medium-emphasis text-body-2">
            F&amp;B Modern
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Self-Order QR Meja</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-qr-scan-2-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Self-Order (Pesan Sendiri via QR Meja)
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Biarkan pelanggan pesan langsung dari HP mereka sendiri, tanpa antre ke kasir
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Menjelaskan cara kerja fitur Self-Order — pelanggan scan QR di meja, pilih menu sendiri
          dari HP mereka, dan pesanan otomatis masuk ke sistem kasir/dapur Anda.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="primary" />
            Apa itu Self-Order dan Mengapa Berguna?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Self-Order</strong> adalah cara pesan mandiri: pelanggan duduk di meja,
              scan kode QR yang tertempel di meja pakai kamera HP mereka, lalu langsung
              melihat menu dan memesan sendiri — mirip cara kerja aplikasi ojek online
              yang menampilkan menu resto, tapi ini khusus untuk pelanggan yang sedang
              duduk di toko Anda.
            </p>
            <p class="text-body-2 mb-3">
              Manfaatnya: mengurangi antrean di kasir saat jam ramai, mempercepat proses
              pemesanan, dan mengurangi salah catat pesanan karena pelanggan input sendiri
              apa yang mereka mau.
            </p>
            <VAlert type="success" variant="tonal" class="mb-0">
              <strong>Alur singkatnya:</strong>
              Scan QR → Isi nama &amp; HP → Pilih menu → Checkout (bayar di kasir / QRIS) →
              Pesanan otomatis masuk ke sistem kasir dan layar dapur (KDS).
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Prasyarat -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-checkbox-circle-line" class="me-2" color="warning" />
            Sebelum Mulai — Yang Harus Sudah Siap
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-table-2">
                <VListItemTitle class="text-body-2">
                  Meja sudah dibuat & punya kode QR — lihat panduan
                  <RouterLink to="/userguide/f-b-modern/tables">
                    Meja &amp; QR Code
                  </RouterLink>.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-restaurant-2-line">
                <VListItemTitle class="text-body-2">
                  Menu &amp; harga produk sudah diisi di Modul Produk.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-fire-line">
                <VListItemTitle class="text-body-2">
                  (Opsional, tapi disarankan) Stasiun dapur/bar sudah disiapkan agar
                  pesanan yang masuk otomatis terarah — lihat panduan
                  <RouterLink to="/userguide/f-b-modern/stations">
                    Stasiun Dapur
                  </RouterLink>.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Penggunaan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-play-circle-line" class="me-2" color="success" />
            Apa yang Dilihat & Dilakukan Pelanggan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Pelanggan duduk di meja, lalu <strong>scan kode QR</strong> yang tertempel
                di meja menggunakan kamera HP.
              </li>
              <li class="mb-3">
                HP mereka otomatis membuka halaman pemesanan toko Anda, menampilkan
                <strong>nama meja</strong> yang mereka duduki.
              </li>
              <li class="mb-3">
                Pelanggan memilih <strong>"Sebagai Tamu"</strong> (isi nama &amp; no. HP saja)
                atau <strong>"Sebagai Member"</strong> (isi no. HP &amp; password akun mereka).
              </li>
              <li class="mb-3">
                Pelanggan menelusuri menu per kategori, klik produk untuk pilih
                <strong>varian</strong> (mis. ukuran Regular/Large), <strong>modifier/topping</strong>
                (mis. extra shot, less ice — kalau produknya punya modifier), jumlah,
                dan catatan (mis. "less sugar").
              </li>
              <li class="mb-3">
                Setelah semua dipilih, pelanggan buka <strong>keranjang</strong>, cek pesanan,
                lalu pilih metode bayar:
                <ul class="mt-2">
                  <li><strong>Bayar di Kasir</strong> — pesanan langsung masuk sebagai
                    "menunggu konfirmasi kasir" di sistem Anda (lihat bagian
                    <em>"Cara Kasir Konfirmasi Bayar di Kasir"</em> di bawah).</li>
                  <li><strong>QRIS / E-Wallet</strong> — muncul kode QR pembayaran di HP
                    mereka; setelah dibayar, pesanan otomatis terkonfirmasi.</li>
                </ul>
              </li>
              <li class="mb-0">
                Pelanggan bisa memantau status pesanan mereka (Menunggu Konfirmasi → Sudah
                Dibayar → Selesai) langsung dari halaman yang sama. Begitu status berubah
                jadi <strong>Sudah Dibayar</strong>, halaman otomatis menampilkan
                <strong>rincian invoice lengkap</strong> (subtotal, diskon, pajak, total,
                metode bayar) di bawah pesanan mereka — tanpa perlu buka halaman lain.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Field identify -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-user-line" class="me-2" color="info" />
            Data yang Diisi Pelanggan (Halaman Identifikasi)
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Tipe</th>
                  <th>Wajib?</th>
                  <th>Penjelasan</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in identifyFields" :key="row.field">
                  <td><strong>{{ row.field }}</strong></td>
                  <td>{{ row.type }}</td>
                  <td>{{ row.wajib }}</td>
                  <td class="text-body-2">{{ row.desc }}</td>
                  <td class="text-body-2">{{ row.example }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Konfirmasi Bayar di Kasir -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-checkbox-circle-line" class="me-2" color="success" />
            💰 Cara Kasir Konfirmasi "Bayar di Kasir"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Kalau pelanggan pilih <strong>"Bayar di Kasir"</strong> saat checkout, pesanan
              masuk ke sistem dengan status <strong>"Pending"</strong> — belum dianggap lunas,
              dan tiket dapur <strong>belum</strong> muncul di KDS. Kasir perlu konfirmasi
              manual begitu pelanggan datang bayar ke meja kasir.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">
                Buka menu <strong>Penjualan → Daftar Order</strong>, cari order dengan
                status <strong>"Pending"</strong> (biasanya nama pelanggan &amp; nomor meja
                sudah tercantum sesuai yang diisi pelanggan saat self-order).
              </li>
              <li class="mb-2">
                Klik order tersebut untuk buka <strong>halaman detail</strong>.
              </li>
              <li class="mb-2">
                Di kartu <strong>"Terima Pembayaran"</strong>, pilih <strong>Metode Pembayaran</strong>
                (Tunai/QRIS/Transfer/Debit/Kredit) sesuai yang benar-benar diterima, dan isi
                <strong>No. Referensi</strong> kalau bukan tunai (opsional).
              </li>
              <li>
                Klik tombol <strong>"Konfirmasi Sudah Dibayar"</strong>.
              </li>
            </ol>
            <VAlert type="success" variant="tonal" class="mb-0 text-body-2">
              Setelah dikonfirmasi: status order berubah jadi <strong>"Sudah Dibayar"</strong>,
              tiket otomatis muncul di layar dapur (KDS), dan halaman status pelanggan
              langsung menampilkan invoice lengkap mereka — semua terjadi otomatis, tidak
              perlu langkah tambahan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert type="error" variant="tonal" icon="ri-error-warning-line">
          <strong>⚠️ PENTING:</strong>
          Sesi pemesanan pelanggan otomatis berakhir setelah beberapa jam tidak
          digunakan (misalnya kalau pelanggan lupa checkout dan sudah pulang).
          Kalau pelanggan mencoba lanjut memesan setelah sesi berakhir, mereka
          akan diminta scan ulang QR meja.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Laminating kode QR di meja</strong> supaya tidak mudah rusak
                  kena tumpahan minuman, dan tambahkan tulisan singkat
                  "Scan - Pilih Menu - Bayar" di sebelahnya.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Pastikan wifi toko cukup kuat</strong> di area tempat duduk
                  pelanggan — self-order butuh koneksi internet aktif di HP pelanggan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Latih kasir untuk rutin cek pesanan "menunggu konfirmasi"</strong>
                  supaya pesanan self-order tidak lama mengendap sebelum diproses dapur.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Meja & QR Code', sub: 'Cetak/atur ulang QR meja', icon: 'ri-table-2', color: 'primary', to: '/userguide/f-b-modern/tables' },
              { title: 'Stasiun Dapur', sub: 'Arahkan pesanan ke stasiun yang tepat', icon: 'ri-restaurant-2-line', color: 'info', to: '/userguide/f-b-modern/stations' },
              { title: 'Kitchen Display', sub: 'Pantau pesanan masuk secara real-time', icon: 'ri-fire-line', color: 'error', to: '/userguide/f-b-modern/kds' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
